import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { smlRoutePath } from '../../../../routing/sml/smlRoutePath';
import { buildQuery } from '../../../../routing/utils/buildQuery';
import {
  profileRouteState,
  profileToolParams,
  type ProfileBannerKind,
  type ProfileRouteParams,
  type ProfileRouteState,
  type ProfileSheet,
  type ProfileTool,
} from './profileRoute';

/** The history state of a step that the Profile pushed: Back (or Close) goes one step back. */
const PUSHED = { profileStep: true } as const;

/** The path of the Profile with its query (no `?` for the page itself). Pure. */
export function profilePath(params: ProfileRouteParams = {}): string {
  const query = Object.keys(params).length === 0 ? '' : buildQuery(params);
  return `/${smlRoutePath(SMLRoutes.promoteProfile)}${query}`;
}

function wasPushed(state: unknown): boolean {
  return typeof state === 'object' && state !== null && (state as { profileStep?: unknown }).profileStep === true;
}

export interface ProfileRoute extends ProfileRouteState {
  openTool: (tool: ProfileTool, banner?: ProfileBannerKind) => void;
  /** The tab of the banners tool: it replaces the URL, so Back still closes the tool. */
  setBanner: (banner: ProfileBannerKind) => void;
  /** Opens the Spotify upload: "Send to all" with only Spotify ticked (`&sheet=spotify`). */
  openSheet: () => void;
  /** Opens the bio upload sheet (`&sheet=spotifyBio`). */
  openBioSheet: () => void;
  /** Opens "Send to all" (`&sheet=sendAll`, design profile-card c). */
  openSendAllSheet: () => void;
  /** Closes the sheet, else the tool: one step back when the Profile pushed it, else the page. */
  close: () => void;
}

/**
 * The view state of Studio → Profile lives in the URL (owner rule: web navigation must work):
 * `?tool=` opens a tool, `&banner=` its tab, `&sheet=spotify|spotifyBio|sendAll` a sheet. A tool and the
 * sheet are history steps, so Back, Forward, a reload and a deep link work. Close goes one step
 * back when the page pushed the step, else it replaces the URL (a deep link has no step before).
 */
export function useProfileRoute(): ProfileRoute {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const state = useMemo(() => profileRouteState(searchParams), [searchParams]);

  const openTool = useCallback(
    (tool: ProfileTool, banner?: ProfileBannerKind) => {
      // eslint-disable-next-line no-void
      void navigate(profilePath(profileToolParams(tool, banner)), { state: PUSHED });
    },
    [navigate]
  );

  const setBanner = useCallback(
    (banner: ProfileBannerKind) => {
      // eslint-disable-next-line no-void
      void navigate(profilePath(profileToolParams('banners', banner)), { replace: true, state: location.state });
    },
    [location.state, navigate]
  );

  const openSheetOf = useCallback(
    (sheet: ProfileSheet) => {
      const params: ProfileRouteParams = { sheet };
      if (state.tool !== null) {
        Object.assign(params, profileToolParams(state.tool, state.tool === 'banners' ? state.banner : undefined));
      }
      // eslint-disable-next-line no-void
      void navigate(profilePath(params), { state: PUSHED });
    },
    [navigate, state.banner, state.tool]
  );
  const openSheet = useCallback(() => openSheetOf('spotify'), [openSheetOf]);
  const openBioSheet = useCallback(() => openSheetOf('spotifyBio'), [openSheetOf]);
  const openSendAllSheet = useCallback(() => openSheetOf('sendAll'), [openSheetOf]);

  const close = useCallback(() => {
    if (wasPushed(location.state)) {
      // eslint-disable-next-line no-void
      void navigate(-1);
      return;
    }
    const params: ProfileRouteParams =
      state.sheet !== null && state.tool !== null
        ? profileToolParams(state.tool, state.tool === 'banners' ? state.banner : undefined)
        : {};
    // eslint-disable-next-line no-void
    void navigate(profilePath(params), { replace: true });
  }, [location.state, navigate, state.banner, state.sheet, state.tool]);

  return useMemo(
    () => ({ ...state, openTool, setBanner, openSheet, openBioSheet, openSendAllSheet, close }),
    [close, openBioSheet, openSendAllSheet, openSheet, openTool, setBanner, state]
  );
}
